/**
 * Entries — a record's place in a list, read from either end. A list's rows are
 * its members (listed by hand), its matches (added by its saved filter) or its
 * contents (both); a record's are its listings, matchings or entries. Every
 * one of those reads returns the same row, an `Entry`, and takes the same
 * `fields` option: the fields the caller shows, sorts, groups or filters by,
 * which decide the optional parts each row's record carries.
 */

import { getFieldType, isReferenceDef, parseLookupPath } from "../fields/fields.js"
import { getListParentFields, getRelationshipFields } from "../fields/relationships.js"
import type {
  EntryRecordFieldsFragment,
  RecordListingFieldsFragment,
} from "../gql/generated/graphql.js"
import type { Record_Links_Bool_Exp, Records_Bool_Exp } from "../gql/generated/schema.js"
import { getType } from "../registries/types.js"
import { recordFixedFields, rollupReads } from "./records.js"

/** The record on an entry: the listed record from a list's end, the list from a record's. */
export type EntryRecord = EntryRecordFieldsFragment
/** A record's listing in a list (its `record_listings` row). */
export type Listing = RecordListingFieldsFragment

export type Entry<R = EntryRecord> = {
  listId: number
  recordId: number
  /** Whether the record is listed by hand. Null when the read doesn't know
   *  (a matches read). */
  listed: boolean | null
  /** Whether the list's saved filter matches the record. Null when the read
   *  doesn't run the filter (a members read). */
  matched: boolean | null
  /** The record's listing in the list; null when it isn't listed, or the read
   *  doesn't say. */
  listing: Listing | null
  record: R
}

/** An entry's collection key: its record, which a list holds once. */
export const entryKey = <E extends Pick<Entry<unknown>, "recordId">>(entry: E): string =>
  String(entry.recordId)

/** A field a caller picks: its key (resolved against the record type), or the
 *  field itself when the caller already has it — a field only some schemas of
 *  the type declare resolves only that way. */
export type FieldPick =
  string | { key: string; type: string; sourceKey?: string | null; path?: string | null }

export interface EntryReadOptions {
  /** The fields the caller reads; every optional part when omitted. */
  fields?: ReadonlyArray<FieldPick>
  /** The type of the entries' records, to resolve picked keys against (a
   *  list's `data.record_type`). Not needed when the picks are fields. */
  recordType?: string | null
  /** Only the rows whose record matches this (a list's rows; a pipeline stage's
   *  deals of one status). */
  where?: Records_Bool_Exp
  /** Whether each record carries its activity: when it last changed and was
   *  last linked to. Per-row aggregates, so only when asked. */
  activity?: boolean
  /** A page of a list's rows: at most `limit`, after skipping `offset`, in the
   *  read's order. Every row when omitted. */
  limit?: number
  offset?: number
}

/** The optional parts of an entry's record, as the queries' variables. */
export interface EntrySelection {
  withLinkedData: boolean
  withContacts: boolean
  withSources: boolean
  withAttachments: boolean
  withTimes: boolean
  withListRelations: boolean
  /** The links to select: every link (`{}`), or those of the picked link fields. */
  linksWhere: Record_Links_Bool_Exp
  withActivity: boolean
}

/** Every part: what a caller that picks no fields gets. */
export const FULL_SELECTION: EntrySelection = {
  withLinkedData: true,
  withContacts: true,
  withSources: true,
  withAttachments: true,
  withTimes: true,
  withListRelations: true,
  linksWhere: {},
  withActivity: false,
}

type Part = "linkedData" | "contacts" | "sources" | "attachments" | "times" | "listRelations"
const PARTS = new Set<string>(["contacts", "sources", "attachments", "times"])

/** A key as a field of `recordType`: its base schema's, a relation or list-parent
 *  field, or a fixed column. Null for a key none of those declare. */
function resolveKey(
  recordType: string | null | undefined,
  key: string
): Exclude<FieldPick, string> | null {
  const declared = recordType
    ? getType(recordType)?.schema?.fields?.find((f) => f.key === key)
    : null
  if (declared) return declared
  return (
    getRelationshipFields().find((f) => f.key === key) ??
    getListParentFields().find((f) => f.key === key) ??
    recordFixedFields.find((f) => f.key === key) ??
    null
  )
}

/** What a field reads beyond the record's columns: the optional parts, and the
 *  keys of the links it follows (a link field's own, a lookup's first step). */
function partsRead(
  field: Exclude<FieldPick, string>,
  schemaId?: number | null
): { parts: Part[]; linkKeys: string[] } {
  const key = field.sourceKey ?? field.key
  if (field.type === "lookup") {
    const via = field.path ? parseLookupPath(field.path)?.via : undefined
    return { parts: ["linkedData"], linkKeys: via ? [via] : [] }
  }
  if (field.type === "rollup")
    return rollupReads(schemaId, field.path) as ReturnType<typeof partsRead>
  if (field.type === "related") return { parts: ["listRelations"], linkKeys: [] }
  const def = getFieldType(field.type)
  const relationship = isReferenceDef(def) ? def.relationship : undefined
  if (relationship === "links") return { parts: [], linkKeys: [key] }
  if (relationship && PARTS.has(relationship))
    return { parts: [relationship as Part], linkKeys: [] }
  return { parts: [], linkKeys: [] }
}

/**
 * The optional parts the picked fields read. A key no field of the type
 * declares may still be a link field of one of its schemas, so it narrows the
 * links to include it — a filter on a key with no links costs nothing.
 */
export function entrySelection(options: EntryReadOptions = {}): EntrySelection {
  const { fields, recordType } = options
  const withActivity = options.activity ?? false
  if (!fields) return { ...FULL_SELECTION, withActivity }
  const parts = new Set<Part>()
  const linkKeys = new Set<string>()
  for (const pick of fields) {
    const field = typeof pick === "string" ? resolveKey(recordType, pick) : pick
    if (!field) {
      linkKeys.add(pick as string)
      continue
    }
    const schemaId =
      (field as { schema_id?: number | null }).schema_id ??
      (recordType ? getType(recordType)?.schema?.id : undefined)
    const read = partsRead(field, schemaId)
    read.parts.forEach((p) => parts.add(p))
    read.linkKeys.forEach((k) => linkKeys.add(k))
  }
  return {
    withLinkedData: parts.has("linkedData"),
    withContacts: parts.has("contacts"),
    withSources: parts.has("sources"),
    withAttachments: parts.has("attachments"),
    withTimes: parts.has("times"),
    withListRelations: parts.has("listRelations"),
    // Sorted, so the same picks in another order make the same variables.
    linksWhere: { schema_key: { _in: [...linkKeys].sort() } },
    withActivity,
  }
}

// ROWS ========================================================================

/** A `record_entries` row: the view's columns, and the listing when selected. */
interface EntriesRow {
  list_id: number | null
  record_id: number | null
  listed: boolean | null
  matched: boolean | null
  listing?: Listing | null
}

/** A `record_entries` row (a list's contents, a record's entries) as an entry. */
export function entryFromEntriesRow<R>(row: EntriesRow, record: R): Entry<R> {
  return {
    listId: Number(row.list_id),
    recordId: Number(row.record_id),
    listed: row.listed ?? false,
    matched: row.matched ?? false,
    listing: row.listing ?? null,
    record,
  }
}

/** A listing (a member) as an entry. A members read doesn't run the filter. */
export function entryFromListing<R>(listing: Listing, record: R): Entry<R> {
  return {
    listId: listing.list_id,
    recordId: listing.record_id,
    listed: true,
    matched: null,
    listing,
    record,
  }
}

/** A match as an entry. A matches read doesn't know whether it's also listed. */
export function entryFromMatch<R>(listId: number, recordId: number, record: R): Entry<R> {
  return { listId, recordId, listed: null, matched: true, listing: null, record }
}
